import { Range, Button, Flex, Input, Label, Span } from '@fareye/ui';
import {
  ddMMMMYYYY,
  hhmmADoMMMYYYY,
  FaDownload,
  startCase,
  cancelToken,
  get,
  HHmm,
  yyyyMmDd
} from '@fareye/utils';
import { parseVoltage, dateFormatter } from '@fareye/common';
import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceArea,
  TooltipProps,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

interface SensorDataPoint {
  datetime: string;
  dateObj: Date;
  weight: number;
}

interface Range {
  start: Date | null;
  end: Date | null;
}

interface IHistoryListing {
  data: any;
  dataLength: number;
  onSelect: (id: any) => void;
  id: { [key: string]: any };
  historySlider: any;
  downloadCsv: any;
  currHistorySlider: any;
  setCurrHistorySlider: any;
  config?: any;
}

export const formatters: any = {
  startCase,
  dateFormatterTZ: dateFormatter
};

export const HEADER_INFLECTION = {
  headers: { 'Key-Inflection': 'camel', 'X-Change-Case': true }
}

export const Loadsensor = ({
  histories,
  data,
  config
}: any) => {
  const {
    config: {
      tabsComponent: {
        history: {
          historyColumns = [],
        }
      }
    }
  } = config


  const [fullData, setFullData] = useState<SensorDataPoint[]>([]);
  const [filteredData, setFilteredData] = useState<SensorDataPoint[]>([]);
  const [range, setRange] = useState<Range>({ start: null, end: null });
  const [tempStart, setTempStart] = useState<Date | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const source = cancelToken().source()

  const getISTDateTime = (utcString: string): { istDate: string; istTime: string } => {
    const date = new Date(utcString);

    const optionsDate: Intl.DateTimeFormatOptions = {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    };

    const optionsTime: Intl.DateTimeFormatOptions = {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };

    const istDate = new Intl.DateTimeFormat("en-CA", optionsDate).format(date); // "YYYY-MM-DD"
    const istTime = new Intl.DateTimeFormat("en-GB", optionsTime).format(date); // "HH:mm"

    return { istDate, istTime };
  };

  const actStartTime = data.actStartTime?.time
  const actEndTime = data.actEndTime?.time
  const currentTime = new Date().toISOString();

  const { istDate: startDate, istTime: start_time } = getISTDateTime(actStartTime);
  const firstData = {
    startDate,
    start_time,
    vehicle_no: data?.vehicleNo,
  };

  
  const { istDate: endDate, istTime: end_time } = actEndTime
  ? getISTDateTime(actEndTime)
  : getISTDateTime(currentTime);

  const lastData = {
    endDate,
    end_time,
  };

  console.log("firstData : ", firstData , " , lastData : ", lastData)

  useEffect(() => {
    const fetchData = async () => {
      const url = `/api/v2/reports/way_point`
      const params = {
        endDate: lastData["endDate"],
        end_time: lastData["end_time"],
        interval: 1,
        startDate: firstData["startDate"],
        start_time: firstData["start_time"],
        'vehicleNo[]': firstData["vehicle_no"], 
        pageNo: 1
      };
      let apiData: any = {}

      const stringParams = Object.fromEntries(
        Object.entries(params).map(([key, value]) => [key, String(value)])
      );

      const queryParams = new URLSearchParams(stringParams).toString();
      const fullUrl = `${url}?${queryParams}`;


      const { data }  = await get(
        fullUrl,
        source.token,
        HEADER_INFLECTION
      )
      const dataArray = data.content ?? [];
      const data_list = dataArray.map((item: any) => {
          const dateObj = new Date(item.istTimestamp);
          return {
            datetime: hhmmADoMMMYYYY(item.istTimestamp),
            dateObj,
            weight: item.extraData?.weight ?? 0,
            };
          });
     setFullData(data_list);
     setFilteredData(data_list);
    }
    fetchData();
  }, []);
  
  const handleClick = (e: any) => {
    if (!e?.activePayload?.[0]) return;
    const point = e.activePayload[0].payload.dateObj;

    if (!tempStart) {
      setTempStart(point);
      setHoverDate(null);
    } else {
      const start = tempStart < point ? tempStart : point;
      const end = tempStart > point ? tempStart : point;

      const filtered = fullData.filter(
        (item) => item.dateObj >= start && item.dateObj <= end
      );

      setRange({ start, end });
      setFilteredData(filtered);
      setTempStart(null);
      setHoverDate(null);
    }
  };

  const handleMouseMove = (e: any) => {
    if (!e?.activePayload?.[0]) return;
    const point = e.activePayload[0].payload.dateObj;
    if (tempStart) {
      setHoverDate(point);
    }
  };

  const handleReset = () => {
    setRange({ start: null, end: null });
    setFilteredData(fullData);
    setTempStart(null);
    setHoverDate(null);
  };

  const refAreaStart = tempStart && hoverDate ? (tempStart < hoverDate ? hhmmADoMMMYYYY(tempStart) : hhmmADoMMMYYYY(hoverDate)) : null;
  const refAreaEnd = tempStart && hoverDate ? (tempStart > hoverDate ? hhmmADoMMMYYYY(tempStart) : hhmmADoMMMYYYY(hoverDate)) : null;

  return (
    <div style={{ width: '100%', height: 600, marginTop: 40 }}>
      <Button onClick={handleReset}
          style={{
            backgroundColor: '#f44336',
            color: '#fff',
            padding: '6px 12px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            Flex:'right',
            marginLeft:'70px'
          }}
        >Reset</Button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ marginLeft: 70 }}>
          Load Sensor Weight Chart{' '}
          {range.start && range.end && (
            <span style={{ color: 'green' }}>
              (Filtered from {hhmmADoMMMYYYY(range.start)} to {hhmmADoMMMYYYY(range.end)})
            </span>
          )}
        </p>
        
      </div>
      {filteredData.length > 0 ? (
        <ResponsiveContainer>
          <LineChart
            data={filteredData}
            margin={{ top: 20, right: 30, left: 70, bottom: 130 }}
            onClick={handleClick}
            onMouseMove={handleMouseMove}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="datetime"
              angle={-45}
              textAnchor="end"
              dy={5}
              minTickGap={10}
            />
            <YAxis tickCount={10}/>
            <Tooltip />
            <Line type="monotone" dataKey="weight" stroke="#8884d8" dot={false} />
            {refAreaStart && refAreaEnd && (
              <ReferenceArea
                x1={refAreaStart}
                x2={refAreaEnd}
                strokeOpacity={0.3}
                fill="#d0e7ff"
                fillOpacity={0.6}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <p>Loading chart...</p>
      )}
    </div>
  );
};
